import { useContext, useEffect, useRef } from "react";
import { useMutation } from "@apollo/client";
import { NotificationContext } from "@solsys/solsys-reactjs";
import { SAVE_SERVICE, saveServiceRequestFromOffline } from "./saveServiceRequest";
import {
  clearServiceBlocked,
  getUnsyncedServices,
  isConnectivityFailure,
  isOfflineServiceHeld,
  isOnline,
  markServiceBlocked,
  markServiceSynced,
  submissionFailureMessage,
} from "../../utils/offlineStorage";

let syncing = null;

function runSync(mutate, { includeBlocked = false, onlyKey = null } = {}) {
  return (async () => {
    const pending = getUnsyncedServices().filter((item) => {
      if (onlyKey && item.key !== onlyKey) return false;
      if (!onlyKey && isOfflineServiceHeld(item.key)) return false;
      return includeBlocked || !item.blocked;
    });
    let successCount = 0;
    let failCount = 0;
    for (const offlineService of pending) {
      if (!isOnline()) {
        failCount += 1;
        continue;
      }
      try {
        const request = saveServiceRequestFromOffline(offlineService);
        const res = await mutate({ variables: { request } });
        if (res.data?.save_service?.id) {
          markServiceSynced(offlineService.key);
          successCount += 1;
        } else {
          failCount += 1;
          markServiceBlocked(offlineService.key, "The server did not confirm this service.");
        }
      } catch (error) {
        console.error("Error syncing offline service:", error);
        failCount += 1;
        if (!isConnectivityFailure(error)) {
          markServiceBlocked(offlineService.key, submissionFailureMessage(error));
        }
      }
    }
    return { successCount, failCount };
  })();
}

function syncPendingServices(mutate, options = {}) {
  if (syncing) {
    return syncing.catch(() => {}).then(() => syncPendingServices(mutate, options));
  }
  // Claim the lock before runSync. mutate() can flush React effects before
  // this function returns, and those effects try to sync again.
  const run = Promise.resolve().then(() => runSync(mutate, options));
  syncing = run;
  run.finally(() => {
    if (syncing === run) syncing = null;
  });
  return run;
}

export default function OfflineServiceSync() {
  const { addNotification } = useContext(NotificationContext);
  const notifyRef = useRef(addNotification);
  notifyRef.current = addNotification;
  const [mutateSaveService] = useMutation(SAVE_SERVICE);
  const mutateRef = useRef(mutateSaveService);
  mutateRef.current = mutateSaveService;

  useEffect(() => {
    let cancelled = false;

    const run = async ({ announceFailure = false, includeBlocked = false, onlyKey = null } = {}) => {
      try {
        if (!isOnline()) return;
        const result = await syncPendingServices(
          (options) => mutateRef.current(options),
          { includeBlocked, onlyKey }
        );
        if (cancelled || !result) return;
        if (result.successCount > 0) {
          notifyRef.current(
            "good",
            result.successCount === 1
              ? "Sent 1 service saved on this device."
              : `Sent ${result.successCount} services saved on this device.`
          );
        }
        if (announceFailure && result.failCount > 0) {
          notifyRef.current(
            "bad",
            result.failCount === 1
              ? "1 saved service could not be sent. It will stay on this device."
              : `${result.failCount} saved services could not be sent. They will stay on this device.`
          );
        }
      } finally {
        window.dispatchEvent(new Event("ecrane-offline-services-sync-done"));
      }
    };

    const onOnline = () => {
      notifyRef.current("good", "Connection restored. Sending services saved on this device.");
      run({ announceFailure: true });
    };
    const onOffline = () => {
      notifyRef.current(
        "bad",
        "You are offline. A service you save stays on this device until the connection returns."
      );
    };
    const onQueued = () => {
      window.setTimeout(() => {
        if (!cancelled) run({ announceFailure: false });
      }, 0);
    };
    const onManual = (event) => {
      const onlyKey = event?.detail?.key || null;
      getUnsyncedServices().forEach((item) => {
        if (onlyKey && item.key !== onlyKey) return;
        clearServiceBlocked(item.key);
      });
      run({ announceFailure: true, includeBlocked: true, onlyKey });
    };

    run({ announceFailure: false });
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    window.addEventListener("ecrane-offline-services-changed", onQueued);
    window.addEventListener("ecrane-offline-services-sync", onManual);
    const interval = window.setInterval(() => run({ announceFailure: false }), 20000);

    return () => {
      cancelled = true;
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      window.removeEventListener("ecrane-offline-services-changed", onQueued);
      window.removeEventListener("ecrane-offline-services-sync", onManual);
      window.clearInterval(interval);
    };
  }, []);

  return null;
}
